import { useMemo } from 'react';
import {
    FIELD_TYPES,
    MARKETPLACES_ENUM,
    useTranslation,
    valueBuilder
} from '../../../../core';
import {
    AMAZON_MARKETPLACE_COUNTRY_ENUM,
    EBAY_MARKETPLACE_COUNTRY_ENUM,
    MARKETPLACE_REGION_ENUM,
    SMTP_PROTOCOL_TYPES
} from '../../../constants';
import { FIELDS, MARKETPLACES_WITH_SHOP_URL, SMTP_FIELDS } from '../utils/constants';
import { formatEnumToSelectOptions } from '../utils/integrationDetailsUtils';

/**
 * Builds the field definitions used by the integration detail tabs.
 *
 * @param {object} integration
 * @param {Function} onChangeProperty
 */
export const useIntegrationFieldGroups = (integration, onChangeProperty) => {
    const { i18n, t } = useTranslation();
    const isEbayMarketplace = integration.marketplace === MARKETPLACES_ENUM.Ebay;
    const isEbayFamilyMarketplace = [
        MARKETPLACES_ENUM.Ebay,
        MARKETPLACES_ENUM.RestApiEbay
    ].includes(integration.marketplace);

    return useMemo(() => {
        const renderSyncTime = value => (
            valueBuilder.getIntegrationSyncTime(value, integration.inProgress, i18n)
        );

        return {
            general: [
                {
                    fieldName: FIELDS.marketplace,
                    label: t('integrations.marketplace'),
                    render: valueBuilder.getMarketplace
                },
                {
                    fieldName: FIELDS.name,
                    label: t('integrations.name'),
                    onChange: isEbayMarketplace ? onChangeProperty(FIELDS.name) : undefined
                },
                {
                    fieldName: FIELDS.login,
                    label: t('integrations.login'),
                    onChange: onChangeProperty(FIELDS.login)
                },
                {
                    fieldName: FIELDS.shopCountry,
                    label: t('integrations.shopCountry'),
                    fieldType: FIELD_TYPES.SELECT,
                    options: formatEnumToSelectOptions(
                        isEbayFamilyMarketplace
                            ? EBAY_MARKETPLACE_COUNTRY_ENUM
                            : AMAZON_MARKETPLACE_COUNTRY_ENUM
                    ),
                    onChange: onChangeProperty(FIELDS.shopCountry),
                    placeholder: t('integrations.shopCountrySelectPlaceholder')
                },
                {
                    fieldName: FIELDS.shopRegion,
                    label: t('integrations.shopRegion'),
                    fieldType: FIELD_TYPES.SELECT,
                    options: formatEnumToSelectOptions(MARKETPLACE_REGION_ENUM),
                    onChange: onChangeProperty(FIELDS.shopRegion),
                    placeholder: t('integrations.shopRegionSelectPlaceholder')
                },
                {
                    fieldName: FIELDS.shopBaseApiUrl,
                    label: t('integrations.shopBaseApiUrl'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(FIELDS.shopBaseApiUrl),
                    placeholder: t('integrations.shopBaseApiUrl'),
                    isVisible: MARKETPLACES_WITH_SHOP_URL.includes(integration.marketplace)
                }
            ],
            synchronization: [
                {
                    fieldName: FIELDS.marketplaceLastSync,
                    label: t('integrations.marketplaceLastSync'),
                    render: renderSyncTime
                },
                {
                    fieldName: FIELDS.marketplaceMessagesLastSync,
                    label: t('integrations.marketplaceMessagesLastSync'),
                    fieldType: FIELD_TYPES.DATE_PICKER,
                    showTime: true,
                    onChange: onChangeProperty(FIELDS.marketplaceMessagesLastSync),
                    render: valueBuilder.getLocalFullTime
                },
                {
                    fieldName: FIELDS.marketplaceOrdersLastSync,
                    label: t('integrations.marketplaceOrdersLastSync'),
                    fieldType: FIELD_TYPES.DATE_PICKER,
                    showTime: true,
                    onChange: onChangeProperty(FIELDS.marketplaceOrdersLastSync),
                    render: valueBuilder.getLocalFullTime
                }
            ],
            blocking: [
                {
                    fieldName: FIELDS.restrictBlock,
                    label: t('integrations.restrictBlock'),
                    fieldType: FIELD_TYPES.CHECKBOX,
                    onChange: onChangeProperty(FIELDS.restrictBlock)
                },
                {
                    fieldName: FIELDS.receiveBlockNotification,
                    label: t('integrations.receiveBlockNotification'),
                    fieldType: FIELD_TYPES.CHECKBOX,
                    onChange: onChangeProperty(FIELDS.receiveBlockNotification)
                }
            ],
            smtp: [
                {
                    fieldName: SMTP_FIELDS.password,
                    label: t('integrations.smtp.password'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(SMTP_FIELDS.password)
                },
                {
                    fieldName: SMTP_FIELDS.smtpServer,
                    label: t('integrations.smtp.smtpServer'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(SMTP_FIELDS.smtpServer)
                },
                {
                    fieldName: SMTP_FIELDS.smtpPort,
                    label: t('integrations.smtp.smtpPort'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(SMTP_FIELDS.smtpPort)
                },
                {
                    fieldName: SMTP_FIELDS.smtpUseSsl,
                    label: t('integrations.smtp.smtpUseSsl'),
                    fieldType: FIELD_TYPES.CHECKBOX,
                    onChange: onChangeProperty(SMTP_FIELDS.smtpUseSsl)
                },
                {
                    fieldName: SMTP_FIELDS.protocolType,
                    label: t('integrations.smtp.protocolType'),
                    fieldType: FIELD_TYPES.SELECT,
                    options: formatEnumToSelectOptions(SMTP_PROTOCOL_TYPES),
                    onChange: onChangeProperty(SMTP_FIELDS.protocolType)
                },
                {
                    fieldName: SMTP_FIELDS.protocolAddress,
                    label: t('integrations.smtp.protocolAddress'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(SMTP_FIELDS.protocolAddress)
                },
                {
                    fieldName: SMTP_FIELDS.protocolPort,
                    label: t('integrations.smtp.protocolPort'),
                    fieldType: FIELD_TYPES.INPUT,
                    onChange: onChangeProperty(SMTP_FIELDS.protocolPort)
                },
                {
                    fieldName: SMTP_FIELDS.protocolUseSsl,
                    label: t('integrations.smtp.protocolUseSsl'),
                    fieldType: FIELD_TYPES.CHECKBOX,
                    onChange: onChangeProperty(SMTP_FIELDS.protocolUseSsl)
                }
            ]
        };
    }, [i18n, integration, isEbayFamilyMarketplace, isEbayMarketplace, onChangeProperty, t]);
};
